// Pure helpers extracted from ScorecardPage's individual stroke-play scoring
// surface so the scoring/edit-round logic can be unit-tested without mounting
// the component. No React, no side effects, no imports — every function here
// is a pure input→output transform.
//
// Covers three bug batches (issues #33–#35 / D-276):
//   - Putts stepper base (#34): putts start UNSET; first '+' → 1, not 3.
//   - GIR derivation + override (#35): auto-fill from strokes/putts, tap to
//     override, back-fill putts when GIR is tapped with putts unset.
//   - Edit-round hole-count gate (#33): never let the count exceed the
//     course's real hole count.

// Putts stepper (#34). Putts start UNSET (rendered as '–'); the first '+'
// yields 1 (base 0), and a hole-out (0 putts) is reached via '+' then '-'.
// Bounds [0, 6]. `current` is the existing putts value, or null/undefined when
// the hole's putts haven't been touched yet. Returns the next integer value.
export function nextPutts(current, delta) {
  const base = Number.isFinite(current) ? current : 0
  return Math.max(0, Math.min(6, base + delta))
}

// Effective score for a hole (GIR default-score fix / #37). The scorecard
// displays a DEFAULT score of the hole's par until the score stepper is
// touched, and Save & Next commits that same default par. So a player who
// leaves the score at par and only adjusts putts still effectively "has" par
// on the hole. GIR derivation must read this effective value — the explicit
// score when the player set one, else the hole's par — or GIR won't light on
// an untouched par hole (the reported bug: GIR only worked after tapping the
// score stepper). Returns `par` (which may be undefined) when no explicit
// score is set; an undefined par flows into deriveGir/girBackfillPutts, whose
// Number.isFinite guards then yield "no GIR" — the correct fall-back when par
// is genuinely unavailable (never guess).
export function effectiveScore(explicitScore, par) {
  return Number.isFinite(explicitScore) ? explicitScore : par
}

// GIR derivation (#35b). Green In Regulation = the green was reached with at
// least two strokes to spare relative to par: strokes_to_green = strokes -
// putts, and 0 <= strokes_to_green <= par - 2. Returns a boolean when both
// strokes and putts are set, or null when it can't be derived (either unset).
export function deriveGir(strokes, putts, par) {
  if (!Number.isFinite(strokes) || !Number.isFinite(putts)) return null
  const p = Number.isFinite(par) ? par : 4
  const strokesToGreen = strokes - putts
  return strokesToGreen >= 0 && strokesToGreen <= (p - 2)
}

// Putts back-fill for the "tap GIR while putts is still unset" shortcut
// (#35d). Sets putts to the value that makes GIR true by definition:
// putts = strokes - (par - 2). Requires strokes to be set; returns null when
// strokes is unset (the GIR tap is then a no-op). Clamped to [0, 6].
export function girBackfillPutts(strokes, par) {
  if (!Number.isFinite(strokes)) return null
  const p = Number.isFinite(par) ? par : 4
  return Math.max(0, Math.min(6, strokes - (p - 2)))
}

// The course's real hole count (#33). Prefer an explicit `holes` count on the
// course record, else the length of holes_data, else the live hole array's
// length, else 18. This is the ceiling for the edit-round selector.
export function courseHoleCount(course, fallbackLen) {
  const explicit = Number(course?.holes)
  if (Number.isFinite(explicit) && explicit > 0) return explicit
  const hd = Array.isArray(course?.holes_data) ? course.holes_data.length : 0
  if (hd > 0) return hd
  return Number.isFinite(fallbackLen) && fallbackLen > 0 ? fallbackLen : 18
}

// Edit-round hole-count options gated by the course's real hole count (#33).
// A 9-hole course offers only '9'; an 18-hole course offers '9' and '18'.
// Never offers a count the course can't support.
export function editHoleOptions(count) {
  const c = Number.isFinite(count) && count > 0 ? count : 18
  const opts = []
  if (c >= 9) opts.push('9')
  if (c >= 18) opts.push('18')
  return opts.length ? opts : [String(c)]
}

// Clamp a requested edit hole-count so it can never exceed the course's real
// hole count (#33 — the guard that stops holes 10–18 being fabricated for a
// 9-hole course in applyEditRound). Falls back to the course count when the
// request is missing/invalid; floors at 1.
export function clampHoleCount(requested, count) {
  const c = Number.isFinite(count) && count > 0 ? count : 18
  const r = Number.isFinite(requested) && requested > 0 ? requested : c
  return Math.max(1, Math.min(r, c))
}

// Honest-save promotion (#36). Builds the next `savedScores` committed
// snapshot that drives the completion dots + score summary. Only players
// whose write ACTUALLY persisted this pass are promoted: `excludedIds` carries
// every player who did NOT genuinely commit — a scored_by conflict (row owned
// by another device) OR a non-network save failure (validation / 4xx / real
// code-or-data bug that was surfaced, not queued for retry). Excluding the
// latter is the fix for the "looks saved but isn't" symptom on the error path:
// without it a failed save still lit the dot and counted the hole in the
// summary even though nothing reached round_scores.
//
// `prev` is the current savedScores; `src` is the scores buffer being saved;
// `playerIds` is who this pass attempted to save. Empty per-player entries
// (all-falsy hole values) never overwrite an existing committed snapshot.
export function promoteSavedScores(prev, src, playerIds, excludedIds) {
  const excluded = excludedIds instanceof Set ? excludedIds : new Set(excludedIds || [])
  const out = { ...(prev || {}) }
  for (const pid of (playerIds || [])) {
    if (excluded.has(pid)) continue
    const committed = {}
    const holes = (src && src[pid]) || {}
    for (const [k, v] of Object.entries(holes)) { if (v) committed[k] = v }
    if (Object.keys(committed).length > 0) out[pid] = committed
  }
  return out
}

// Classify a per-player standalone-score save result. `upsert_standalone_score`
// returns `{ error }` at HTTP 200 (non-throwing) for conditions the client must
// NOT treat as a commit. Returns true only when the write genuinely reached
// round_scores (its completion dot may light per honest-save #36):
//   • 'scored_by_conflict' — the row is owned by another device right now.
//   • 'round_deleted'      — the round's creator soft/hard-deleted it (D-279);
//                            the server's resurrection guard refused the write.
// Excluding 'round_deleted' is the fix for a co-player whose saves are silently
// rejected in the delete→handoff gap: without it the dot lights on a hole that
// never reached round_scores — the exact "looks saved but isn't" (#36) symptom.
export function saveResultCommitted(saveRes) {
  const err = saveRes?.error
  return err !== 'scored_by_conflict' && err !== 'round_deleted'
}

// ── F-077: single-device group scoring — per-player played-hole logic ────────
// One person scores everyone on one phone. The fixes below stop a player who
// stopped early (or was marked finished in Edit Round) from having later holes
// fabricated as pars — the union-across-the-group fill was the fabrication.

// The hole numbers a single player actually has a recorded score on, sorted
// ascending. `includeCurrent` adds the current hole even when untouched — the
// scorer is standing on it and the par default is their deliberate value (the
// same assumption Save & Next commits on advance). Pass includeCurrent:false
// for a player who has stopped, so their row freezes at their last real hole.
export function playerPlayedHoleNumbers(playerScores, { currentHole, includeCurrent } = {}) {
  const set = new Set()
  const s = playerScores || {}
  for (const [k, v] of Object.entries(s)) {
    if (v == null || v === '') continue
    const n = Number(k)
    if (Number.isFinite(n)) set.add(n)
  }
  if (includeCurrent && Number.isFinite(Number(currentHole))) set.add(Number(currentHole))
  return [...set].sort((a, b) => a - b)
}

// Return a copy of `existing` with par committed on exactly `holeNumbers` that
// have no score yet. `parForHole` is either a number or a (holeNum)=>par fn.
// Never touches holes outside `holeNumbers` — so it can only fill a player's
// OWN played holes, never the group union.
export function parFillHoles(existing, holeNumbers, parForHole) {
  const next = { ...(existing || {}) }
  for (const h of (holeNumbers || [])) {
    const k = String(h)
    if (next[k] == null || next[k] === '') {
      const par = typeof parForHole === 'function' ? parForHole(h) : parForHole
      next[k] = Number.isFinite(par) ? par : 4
    }
  }
  return next
}

// ── D-346: in-round player ⋮ menu — Add player / Replace-Swap / back-entry ────
// Pure, unit-testable helpers for the in-round per-player menu. No React, no
// DB — every function is an input→output transform mirroring the app's
// existing seat/score conventions.

// Guest-only Replace/Swap gate. A round SEAT may be replaced ONLY when the
// underlying players row is a GUEST/stub — players.auth_user_id IS NULL. An
// authenticated account (auth_user_id set) can never be replaced (v1 scope).
// `authUserId` is the seat's players.auth_user_id as read from round_scores'
// joined players row: null ⇒ guest ⇒ replaceable; any uuid ⇒ authenticated.
export function canReplaceSeat(authUserId) {
  return authUserId == null
}

// D-346 · Replace/Swap option 3 (Delete a guest seat's round) calls
// remove_guest_seat, which is OWNER-GATED server-side: the handler resolves the
// round owner from the VERIFIED JWT and rejects any caller who is not
// standalone_rounds.owner_player_id. Options 1 (transfer/rename) and 2
// (keep-and-finish) are NOT owner-gated and work for any co-scorer, so only
// option 3 must be gated client-side — otherwise a non-owner co-scorer sees a
// third fully-enabled option that dead-ends with a generic error toast.
// roundOwnerId is stamped at round-start (standaloneState.player_id) and rides
// in the resume payload, so on the owner's device it equals myPlayerId. When
// it's absent (a legacy payload with no owner stamp) we fail OPEN and let the
// server's authoritative check decide — matching the existing "Delete All
// Players" gate convention (client gating is UX only). Fails CLOSED when the
// owner is known but this device's player id is not yet resolved.
export function isRoundOwner(roundOwnerId, myPlayerId) {
  if (roundOwnerId == null) return true
  return roundOwnerId === myPlayerId
}

// Build a { player_id → isGuest(bool) } map from the hydrated round_scores
// rows. A seat is guest-eligible ONLY when we POSITIVELY resolved its joined
// players.auth_user_id and it is null. Rows whose auth_user_id column is
// absent (an older read path / preview env that didn't SELECT it yet) are left
// UNSET — the caller then treats them as not-guest (Replace hidden), failing
// closed so a real account is never mistaken for a guest. `authKey` is the row
// property carrying the joined auth_user_id (default 'player_auth_user_id').
export function buildSeatGuestMap(rows, authKey = 'player_auth_user_id') {
  const map = {}
  for (const r of (rows || [])) {
    if (!r || r.player_id == null) continue
    if (Object.prototype.hasOwnProperty.call(r, authKey)) {
      map[r.player_id] = r[authKey] === null || r[authKey] === undefined
    }
  }
  return map
}

// Compute the scorecard's INITIAL per-player scoring state on open. The host
// (round creator) auto-scores everyone: init_standalone_round stamps every
// seat's round_scores.scored_by to the owner at round creation, so on the
// host's device the scorecard must render those seats as scoring BY DEFAULT and
// must NOT reset them to not-scoring. Rules, per seat:
//   * scored_by == null    → scoring (unclaimed; I'm the default scorer)
//   * scored_by === meId   → scoring (I own this row)
//   * scored_by === other  → NOT scoring ("Scored by <name>", another device)
// `scoredByLookup` is { player_id → scored_by|null }; `meId` is THIS device's
// resolved player id (the fetchSetup effect is gated on it, so it is present).
// Regression guard (defects 2 & 6): the caller must have the hydrated rows in
// scope — a block-scoped read that threw left this map empty and every seat
// fell through to not-scoring.
export function initialScoringState(members, scoredByLookup, meId) {
  const out = {}
  for (const m of (members || [])) {
    if (!m || m.player_id == null) continue
    const owner = scoredByLookup ? scoredByLookup[m.player_id] : null
    out[m.player_id] = owner == null || owner === meId
  }
  return out
}

// The newcomer's starting hole when a replacement joins mid-round: the round's
// CURRENT hole. Earlier holes 1..X-1 are left BLANK (absent from hole_scores)
// and are excluded from totals/leaderboard — no score, no penalty. Falls back
// to hole 1 for a missing/invalid current hole.
export function newcomerStartHole(currentHole) {
  const n = Number(currentHole)
  return Number.isFinite(n) && n > 0 ? n : 1
}

// The hole numbers a manual back-entry UI should collect for a newcomer who
// started on `currentHole` — every earlier hole [firstHole .. currentHole-1].
// `firstHole` defaults to 1 (a back-9 round would pass 10). Empty when the
// newcomer joined on the first hole (nothing earlier to enter).
export function backEntryHoleNumbers(currentHole, firstHole = 1) {
  const x = Number(currentHole)
  const start = Number.isFinite(Number(firstHole)) ? Number(firstHole) : 1
  if (!Number.isFinite(x) || x <= start) return []
  const out = []
  for (let h = start; h < x; h++) out.push(h)
  return out
}

// Build the hole_scores map for a manual back-entry pass. `entries` is a
// { holeNum → strokes } object from the entry UI; only positive integer
// strokes survive — blank / zero / non-numeric holes are DROPPED entirely
// (absent = not played, never a zero-penalty). Keys are normalized to string
// hole numbers, matching the hole_scores jsonb shape.
export function buildBackEntryHoleMap(entries) {
  const out = {}
  for (const [k, v] of Object.entries(entries || {})) {
    const hole = Number(k)
    const strokes = Number(v)
    if (Number.isFinite(hole) && hole > 0 && Number.isFinite(strokes) && strokes > 0) {
      out[String(hole)] = Math.round(strokes)
    }
  }
  return out
}

// Build a brand-new guest seat's standaloneState.players payload entry, mirror
// of PlayPage's per-player record (team 0, no handicap, round-level tee via
// null). `slot` is the next dense index; `name` defaults to a positional
// placeholder. `linkedPlayerId` is the freshly-created stub's id.
export function buildNewSeatPayload(slot, name, linkedPlayerId) {
  const s = Number.isFinite(Number(slot)) ? Number(slot) : 0
  return {
    slot: s,
    name: (name && String(name).trim()) || `Player ${s + 1}`,
    phone: null,
    linked_player_id: linkedPlayerId || null,
    team: 0,
    use_handicap: false,
    handicap_index: null,
    tee: null,
  }
}

// Build the membersRef/groupPlayers entry for a new seat — the { player_id,
// name } shape the scorecard renders and the save loop iterates.
export function buildNewSeatMember(name, linkedPlayerId) {
  return { player_id: linkedPlayerId || null, name: (name && String(name).trim()) || 'Player' }
}

// Sum ONLY holes with an entered (truthy, positive-integer) stroke value —
// blank holes are excluded entirely, never zero-filled. Mirrors the save-path
// total in saveProgress (`if (v) holeScores[k] = parseInt(v)` then sum). Used
// to assert a mid-round joiner is scored on played holes only.
export function sumEnteredHoles(holeScores) {
  let total = 0
  for (const v of Object.values(holeScores || {})) {
    const n = parseInt(v)
    if (v && Number.isFinite(n)) total += n
  }
  return total
}

// score_vs_par over ONLY entered holes, using the same blank-exclusion rule as
// sumEnteredHoles. `parForHole` is a number or (holeNum)=>par fn (default par
// 4 when unknown). A newcomer with blank earlier holes therefore carries NO
// phantom over/under-par for the holes they didn't play.
export function sumVsParEntered(holeScores, parForHole) {
  let vs = 0
  for (const [k, v] of Object.entries(holeScores || {})) {
    const n = parseInt(v)
    if (!v || !Number.isFinite(n)) continue
    const par = typeof parForHole === 'function' ? parForHole(Number(k)) : parForHole
    vs += n - (Number.isFinite(par) ? par : 4)
  }
  return vs
}

// Balanced two-row split for the in-round per-tee yardage list (rework v2). When
// the list of `n` tees would wrap to a second line, split it into two centered
// rows with the LARGER half on top: ceil(n/2) on the top row, floor(n/2) on the
// bottom (5→[3,2], 4→[2,2], 3→[2,1], 2→[1,1], 1→[1,0], 0→[0,0]). Pure so the
// split math is unit-testable; the render-side overflow MEASURE that decides
// WHETHER to split lives in ScorecardPage (untestable in the DOM-less unit suite)
// and only calls this to compute the two slice sizes once it has decided to wrap.
export function splitBalanced(n) {
  const c = Number.isFinite(Number(n)) && Number(n) > 0 ? Math.floor(Number(n)) : 0
  const top = Math.ceil(c / 2)
  return [top, c - top]
}

// The still-selected players a "Save All Players" would file as INCOMPLETE on
// an individual stroke round — those whose committed hole count is below the
// round's full hole count. Drives the pre-save confirm prompt (F-077 AC2).
// Uses the SAME played-hole definition as the per-player save fill so the
// counts shown match what actually gets saved: a not-finished player counts the
// current hole (they're on it); a finished player is frozen at their last real
// hole. Players not being scored are ignored.
export function incompleteScoringPlayers(playerList, scores, scoringFor, fullHoleCount, opts = {}) {
  const { currentHole, playersDone = {} } = opts
  const full = Number.isFinite(Number(fullHoleCount)) && Number(fullHoleCount) > 0 ? Number(fullHoleCount) : 18
  const out = []
  for (const m of (playerList || [])) {
    const pid = m?.player_id
    if (pid == null || !scoringFor?.[pid]) continue
    const count = playerPlayedHoleNumbers(scores?.[pid], {
      currentHole,
      includeCurrent: !playersDone[pid],
    }).length
    if (count < full) out.push({ player_id: pid, name: m.name || 'Player', holeCount: count })
  }
  return out
}
